/**
 * SalaryTab (02.09, владелец: «пора запускать вкладку зарплаты у операторов»)
 * — своя зарплата оператора за период. Периоды как у офиса: текущая неделя
 * (пт–чт), прошлая, месяц + «‹ ›» по неделям. Сервер сам режет выдачу до
 * своего staff_id (право salary_own). Сверху итог и разбивка, ниже по дням:
 * игры (ставка, такси, «без отчёта», отмена ₽200), МК, админство, допработы,
 * штрафы. Цвета строк — как в HR «Зарплаты» (игры жёлтые / МК свой цвет --mc /
 * админство фуксия / допработы фиолет — 11.10). 04.09 (владелец): покупки для
 * компании — компенсация вместе с ЗП, «особым цветом» lime (.bX/.rX).
 * 05.09 (владелец): премии — плюс к ЗП, синим var(--blue) (.bB/.rB), между
 * покупками и штрафами.
 * 13.10 (архив 1С, ПР3): игры 1С и всё до 01.03.2026 выплачены через 1С — строка дня с подписью и итог
 * «Начислено (выплачено через 1С)» под «К выплате», в саму сумму к выплате не входят; такси отчёта 1С — «доп 1С».
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import type { OperatorSalary, SalaryDay, SalaryRange } from "../../hooks/useSalary";
import { addDays, fmtDateApi, parseDateApi } from "../../utils/date";
import { PAID_1C_LABEL, PAID_1C_SHORT, TAXI_1C_LABEL, withPaid1c } from "@ub/shared/archive1c";
import css from "./salaryTab.module.css";
import { EwPayNote } from "../oo/EwPayNote";
import type { EwPayInfo } from "../oo/ewPayText";
import { HoldCard } from "./HoldCard";
import { SlipMarkTag, slipRowClass, slipWrapClass, type SlipTone } from "../../components/shared/SlipMark";

type Preset = "current" | "previous" | "month";
interface Weeks { current: SalaryRange; previous: SalaryRange; month: SalaryRange; today: string }
const NB = " ";
const fmt = (n: number) => `${Math.round(n || 0).toLocaleString("ru-RU").replace(/ |,/g, NB)}`;
const t5 = (t?: string) => (t ?? "").slice(0, 5);
const WD = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
const dayLabel = (iso: string) => { const d = parseDateApi(iso); return `${d.getUTCDate()}.${String(d.getUTCMonth() + 1).padStart(2, "0")} ${WD[d.getUTCDay()]}`; };
const rangeLabel = (r: SalaryRange) => `${dayLabel(r.from).slice(0, 5)} – ${dayLabel(r.to).slice(0, 5)}`;

/**
 * range/onSetRange — период живёт у хоста (OperatorScreen): его меняет и
 * стандартный календарь в шапке (режим «период»), и пресеты здесь.
 */
export function SalaryTab({ operatorId, range, onSetRange }: {
  operatorId: string | null; range: SalaryRange | null; onSetRange: (from: string, to: string) => void;
}) {
  const [weeks, setWeeks] = useState<Weeks | null>(null);
  const [data, setData] = useState<OperatorSalary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { api<Weeks>("/api/finance/salary-weeks").then(setWeeks).catch(() => setError("Не удалось загрузить периоды")); }, []);
  /* первый заход — текущая неделя пт–чт (пока в календаре ничего не выбрано) */
  useEffect(() => { if (weeks && !range) onSetRange(weeks.current.from, weeks.current.to); }, [weeks, range, onSetRange]);
  const preset: Preset | null = weeks && range
    ? ((["current", "previous", "month"] as const).find((p) => weeks[p].from === range.from && weeks[p].to === range.to) ?? null) : null;

  useEffect(() => {
    if (!range || !operatorId) return;
    let alive = true; setLoading(true);
    /* 03.10: view=operator — кабинет показывает то, что видит сам оператор (без неотправленного черновика), кто бы ни смотрел;
       staff_id + без «первого попавшегося» — раньше пустая неделя показывала зарплату другого человека */
    api<{ results?: OperatorSalary[] }>(`/api/finance/salary?from=${range.from}&to=${range.to}&staff_id=${encodeURIComponent(operatorId)}&view=operator`)
      .then((r) => { if (!alive) return; setData((r.results ?? []).find((s) => s.staffId === operatorId) ?? null); setError(null); })
      .catch(() => { if (alive) setError("Не удалось загрузить зарплату"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [range?.from, range?.to, operatorId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* «‹ ›» — сдвиг на длину периода */
  const shift = (dir: -1 | 1) => {
    if (!range) return;
    const f0 = parseDateApi(range.from); const t0 = parseDateApi(range.to);
    const len = Math.round((t0.getTime() - f0.getTime()) / 86400000) + 1;
    onSetRange(fmtDateApi(addDays(f0, len * dir)), fmtDateApi(addDays(t0, len * dir)));
  };
  const days = useMemo(() => Object.entries(data?.days ?? {}).sort(([a], [b]) => a.localeCompare(b)), [data]);

  return (
    <div className={css.root}>
      <div className={css.periods}>
        <button type="button" className={css.arrow} onClick={() => shift(-1)} aria-label="Период назад">‹</button>
        {(["previous", "current", "month"] as const).map((p) => (
          <button key={p} type="button" className={`${css.chip} ${preset === p ? css.on : ""}`} disabled={!weeks}
            onClick={() => weeks && onSetRange(weeks[p].from, weeks[p].to)}>
            {p === "previous" ? "Прошлая" : p === "current" ? "Эта неделя" : "Месяц"}
          </button>
        ))}
        <button type="button" className={css.arrow} onClick={() => shift(1)} aria-label="Период вперёд">›</button>
      </div>
      {range ? <div className={css.rangeLbl}>{rangeLabel(range)}{preset === "current" || preset === "previous" ? " · пт–чт" : preset === "month" ? " · месяц" : " · выбранный период"}</div> : null}
      {error ? <div className={css.err}>{error}</div> : null}
      {loading && !data ? <div className={css.empty}>Считаем…</div> : null}
      {data ? (
        <>
          <div className={css.total}>
            <span className={css.totalLbl}>К выплате</span>
            <span className={css.totalSum}>₽{fmt(data.total)}</span>
          </div>
          <HoldCard />{/* 16.10: холд на сегодня — от периода не зависит */}
          <div className={css.breakdown}>
            {([
              ["Игры", data.quest_earnings, css.bQ, `${data.games_count} игр`],
              ["МК", data.mc_earnings, css.bM, ""],
              ["Админство", data.hall_admin_earnings, css.bA, ""],
              ["Допработы", data.extra_work_earnings, css.bE, ""],
              ["Такси", data.taxi_earnings, css.bT, ""],
              ["Покупки", data.purchase_total, css.bX, "компенсация"],
              ["Премии", data.bonus_total, css.bB, ""],
            ] as [string, number, string, string][]).filter(([, v]) => v > 0).map(([l, v, cls, sub]) => (
              <div key={l} className={`${css.bItem} ${cls}`}><span className={css.bLbl}>{l}{sub ? ` · ${sub}` : ""}</span><b>₽{fmt(v)}</b></div>
            ))}
            {data.penalty_total > 0 ? <div className={`${css.bItem} ${css.bP}`}><span className={css.bLbl}>Штрафы</span><b>−₽{fmt(data.penalty_total)}</b></div> : null}
          </div>
          {(data.paid_1c_total ?? 0) > 0 ? <div className={css.paid1c}><span>{PAID_1C_LABEL}</span><b>₽{fmt(data.paid_1c_total ?? 0)}</b><span className={css.paid1cSub}>в «К выплате» не входит</span></div> : null}
          {days.length ? days.map(([d, day]) => <DayBlock key={d} iso={d} day={day} />) : <div className={css.empty}>За этот период начислений нет.</div>}
          <div className={css.note}>Суммы предварительные — до проверки отчётов офисом. Вопросы по ставке или отчёту — в ОО.</div>
        </>
      ) : !loading ? <HoldCard /> : null}
    </div>
  );
}

function DayBlock({ iso, day }: { iso: string; day: SalaryDay }) {
  /* 15.10 (владелец): ew — разбивка оплаты допработы, второй строкой «оплачено 4,5 ч: окно 6 ч минус игра 17:00 (1,5 ч)» */
  /* 26.10 (владелец: «особо отмечать в расчётке»): mark/tone — особая отметка премии или штрафа (SlipMark) */
  const rows: { cls: string | undefined; l: string; r: string; sub?: string; warn?: boolean; ew?: EwPayInfo; mark?: string | null; tone?: SlipTone }[] = [];
  /* МК своей игры — сразу под ней (отзыв оператора 02.09) */
  const mcs = [...day.mc];
  const takeMc = (q: { short_name: string; time: string }) => {
    const i = mcs.findIndex((m) => (m.quest_short || "") === (q.short_name || "") && (!m.time || t5(m.time) === t5(q.time)));
    return i >= 0 ? mcs.splice(i, 1)[0] : undefined;
  };
  for (const q of day.quests) {
    const cancelled = (q as { status?: string }).status === "cancelled";
    rows.push({
      cls: css.rQ, l: `${q.time.slice(0, 5)} ${q.short_name || q.quest_name}`,
      r: q.zero_rate ? "₽0" : `₽${fmt(q.rate)}`,
      sub: withPaid1c(cancelled ? "отмена — компенсация" : !q.has_report ? "отчёт не сдан" : undefined, q.paid_1c), warn: !cancelled && !q.has_report,
    });
    for (let m = takeMc(q); m; m = takeMc(q)) rows.push({ cls: css.rM, l: `↳ МК ${m.mc_name}`, r: `₽${fmt(m.rate)}`, sub: m.paid_1c ? PAID_1C_SHORT : undefined });
    if (q.taxi_amount > 0) rows.push({ cls: css.rT, l: q.taxi_1c ? TAXI_1C_LABEL : "такси", r: `₽${fmt(q.taxi_amount)}`, sub: q.paid_1c ? PAID_1C_SHORT : undefined });
  }
  for (const m of mcs) rows.push({ cls: css.rM, l: `${(m.time || "").slice(0, 5)} МК ${m.mc_name}${m.quest_short ? ` · ${m.quest_short}` : ""}`, r: `₽${fmt(m.rate)}`, sub: m.paid_1c ? PAID_1C_SHORT : undefined });
  if (day.hall_admin) rows.push({ cls: css.rA, l: `Админство ${String(day.hall_admin.branch || "").toUpperCase()} · ${day.hall_admin.hours} ч`, r: `₽${fmt((day.hall_admin as { earning?: number }).earning ?? day.hall_admin.hours * day.hall_admin.rate)}` });
  for (const e of day.extra_work ?? []) {
    rows.push({ cls: css.rE, l: `Доп ${e.time_from.slice(0, 5)}–${e.time_to.slice(0, 5)}${e.description ? ` · ${e.description.slice(0, 28)}` : ""}`, r: `₽${fmt(e.earning)}`, ew: e });
    if (e.taxi > 0) rows.push({ cls: css.rT, l: "такси", r: `₽${fmt(e.taxi)}` });
  }
  /* 04.09 (владелец): подтверждённые покупки — между допработами и штрафами, lime */
  for (const p of day.purchases ?? []) rows.push({ cls: css.rX, l: `🧾 Покупка · ${p.description}${p.comment ? ` — ${p.comment.slice(0, 24)}` : ""}`, r: `₽${fmt(p.amount)}` });
  /* 05.09 (владелец): премии — между покупками и штрафами, синим */
  for (const b of day.bonuses ?? []) rows.push({ cls: css.rB, l: `🎁 Премия · ${b.reason}`, r: `₽${fmt(b.amount)}`, mark: b.mark, tone: "bonus" });
  for (const p of day.penalties ?? []) rows.push({ cls: css.rP, l: `Штраф · ${p.reason}`, r: `−₽${fmt(Math.abs(p.amount))}`, mark: p.mark, tone: "pen" });
  if (!rows.length) return null;
  return (
    <section className={css.day}>
      <div className={css.dayHead}><span>{dayLabel(iso)}</span><b>₽{fmt(day.day_total)}</b></div>
      {rows.map((r, i) => (
        <div key={i} className={`${css.row} ${r.cls ?? ""} ${r.tone ? slipRowClass(r.mark, r.tone) : ""}`}>
          <span className={`${css.rowL} ${r.tone ? slipWrapClass(r.mark) : ""}`}>{r.tone ? <SlipMarkTag mark={r.mark} tone={r.tone} /> : null}{r.l}{r.sub ? <span className={`${css.rowSub} ${r.warn ? css.warn : ""}`}> · {r.sub}</span> : null}{r.ew ? <EwPayNote e={r.ew} /> : null}</span>
          <span className={css.rowR}>{r.r}</span>
        </div>
      ))}
    </section>
  );
}
